import React, { useState } from 'react';

const DECLINE_REASONS = [
  'Photo quality too low',
  'Caption needs more detail',
  'Content not suitable for sharing',
  'Duplicate moment already submitted',
  'Missing GDPR confirmation',
];

const s = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '16px',
  },
  modal: {
    background: '#fff', borderRadius: '14px', width: '100%', maxWidth: '560px',
    maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 16px 48px rgba(0,0,0,0.25)',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '18px 20px 14px', borderBottom: '1px solid #f0f0f0', position: 'sticky', top: 0, background: '#fff', zIndex: 1,
  },
  title: { fontSize: '17px', fontWeight: '700', color: '#222' },
  closeBtn: {
    width: '32px', height: '32px', borderRadius: '50%', background: '#f0f0f0',
    border: 'none', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  body: { padding: '20px' },
  meta: { fontSize: '13px', color: '#888', marginBottom: '16px' },
  catBadge: {
    display: 'inline-block', background: '#f3e5f5', color: '#6a1b9a',
    borderRadius: '20px', padding: '3px 12px', fontSize: '12px', fontWeight: '600', marginBottom: '16px',
  },
  caption: { fontSize: '15px', color: '#333', lineHeight: '1.65', marginBottom: '20px' },
  sectionLabel: { fontSize: '11px', fontWeight: '700', color: '#DB3267', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '10px' },

  // Photo viewer
  mainPhotoWrap: { position: 'relative', borderRadius: '10px', overflow: 'hidden', marginBottom: '10px', background: '#000', textAlign: 'center' },
  mainPhoto: { width: '100%', maxHeight: '320px', objectFit: 'contain', display: 'block' },
  navBtn: {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)',
    background: 'rgba(0,0,0,0.45)', color: '#fff', border: 'none',
    width: '36px', height: '36px', borderRadius: '50%', fontSize: '18px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  thumbRow: { display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' },
  thumb: { width: '64px', height: '64px', objectFit: 'cover', borderRadius: '6px', cursor: 'pointer', border: '2px solid transparent' },
  thumbActive: { border: '2px solid #DB3267' },
  downloadBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '7px 14px', background: '#f7f7f9', border: '1px solid #e0e0e0',
    borderRadius: '8px', fontSize: '13px', color: '#555', cursor: 'pointer', marginBottom: '20px', textDecoration: 'none',
  },

  // Actions
  actions: { display: 'flex', gap: '10px', marginTop: '4px', flexWrap: 'wrap' },
  approveBtn: {
    flex: 1, padding: '12px', background: '#e8f5e9', color: '#2e7d32',
    borderRadius: '8px', fontWeight: '700', fontSize: '14px', border: '1.5px solid #a5d6a7',
  },
  declineBtn: {
    flex: 1, padding: '12px', background: '#fff', color: '#888',
    borderRadius: '8px', fontWeight: '600', fontSize: '14px', border: '1.5px solid #e0e0e0',
  },
  declinePanel: { marginTop: '14px', padding: '14px', background: '#f7f7f9', borderRadius: '8px' },
  declinePanelTitle: { fontSize: '12px', fontWeight: '700', color: '#555', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  reasonBtn: {
    display: 'block', width: '100%', textAlign: 'left', padding: '9px 12px',
    background: '#fff', border: '1px solid #e0e0e0', borderRadius: '6px',
    marginBottom: '6px', fontSize: '13px', color: '#444', cursor: 'pointer',
  },
  cancelBtn: { padding: '8px 14px', background: 'none', color: '#888', fontSize: '13px', border: 'none', cursor: 'pointer' },
  statusApproved: { color: '#2e7d32', fontWeight: '700', fontSize: '14px', padding: '10px 0' },
  statusDeclined: { color: '#888', fontSize: '14px', padding: '10px 0' },
  reasonTag: { fontSize: '13px', color: '#888', fontStyle: 'italic' },
};

export default function SubmissionModal({ sub, onClose, onApprove, onDecline }) {
  const [photoIndex, setPhotoIndex] = useState(0);
  const [showDecline, setShowDecline] = useState(false);

  const hasPhotos = Array.isArray(sub.photos) && sub.photos.length > 0;
  const currentPhoto = hasPhotos ? sub.photos[photoIndex] : null;

  function prev() { setPhotoIndex(i => (i - 1 + sub.photos.length) % sub.photos.length); }
  function next() { setPhotoIndex(i => (i + 1) % sub.photos.length); }

  function handleApprove() { onApprove(sub.id); onClose(); }
  function handleDecline(reason) { onDecline(sub.id, reason); onClose(); }

  return (
    <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={s.modal}>

        <div style={s.header}>
          <div style={s.title}>{sub.about}</div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={s.body}>
          <div style={s.meta}>
            Submitted by <strong>{sub.submittedBy}</strong> &nbsp;·&nbsp; {sub.shift} shift &nbsp;·&nbsp; {sub.submittedAt}
          </div>

          <div style={s.catBadge}>{sub.category}</div>

          <div style={s.sectionLabel}>What happened</div>
          <div style={s.caption}>{sub.caption}</div>

          {hasPhotos && (
            <>
              <div style={s.sectionLabel}>Photos ({sub.photos.length})</div>

              <div style={s.mainPhotoWrap}>
                <img src={currentPhoto.url} alt="" style={s.mainPhoto} />
                {sub.photos.length > 1 && (
                  <>
                    <button style={{ ...s.navBtn, left: '8px' }} onClick={prev}>‹</button>
                    <button style={{ ...s.navBtn, right: '8px' }} onClick={next}>›</button>
                  </>
                )}
              </div>

              {sub.photos.length > 1 && (
                <div style={s.thumbRow}>
                  {sub.photos.map((p, i) => (
                    <img
                      key={i}
                      src={p.url}
                      alt=""
                      style={{ ...s.thumb, ...(i === photoIndex ? s.thumbActive : {}) }}
                      onClick={() => setPhotoIndex(i)}
                    />
                  ))}
                </div>
              )}

              <a
                href={currentPhoto.url}
                download={`care-moment-${sub.about}-photo-${photoIndex + 1}.jpg`}
                style={s.downloadBtn}
              >
                ⬇ Download photo {sub.photos.length > 1 ? `${photoIndex + 1} of ${sub.photos.length}` : ''}
              </a>
            </>
          )}

          {sub.status === 'pending' && (
            <>
              <div style={s.actions}>
                <button style={s.approveBtn} onClick={handleApprove}>✓ Approve &amp; Share</button>
                <button style={s.declineBtn} onClick={() => setShowDecline(v => !v)}>Decline</button>
              </div>

              {showDecline && (
                <div style={s.declinePanel}>
                  <div style={s.declinePanelTitle}>Select a reason</div>
                  {DECLINE_REASONS.map(r => (
                    <button key={r} style={s.reasonBtn} onClick={() => handleDecline(r)}>{r}</button>
                  ))}
                  <button style={s.cancelBtn} onClick={() => setShowDecline(false)}>Cancel</button>
                </div>
              )}
            </>
          )}

          {sub.status === 'approved' && <div style={s.statusApproved}>✓ Approved &amp; shared</div>}

          {sub.status === 'declined' && (
            <div>
              <div style={s.statusDeclined}>✕ Declined</div>
              {sub.declineReason && <div style={s.reasonTag}>Reason: {sub.declineReason}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
